import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon, 
  AutoAwesome as AutoAwesomeIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

export interface HeaderProps {
  isAuthenticated: boolean;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ isAuthenticated, onLogout }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };
  
  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
      }}
    >
      <Toolbar sx={{ 
        py: 1, 
        px: { xs: 2, md: 4 },
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'space-between'
      }}>
        {/* Logo */}
        <Typography
          variant="h5"
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            fontWeight: 800,
            mr: 4,
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(90deg, #1a365d 0%, #2d8cff 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            flexShrink: 0
          }}
        >
          Contract AI
        </Typography>
        
        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={handleMobileMenuOpen}
            sx={{ 
              ml: 'auto', 
              mr: 1,
              color: '#1a365d',
              display: { xs: 'flex', md: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        {/* Navigation - Desktop */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center',
            flexGrow: 1,
            justifyContent: 'center',
            '& > a': {
              mx: 0.5  // Reduce margin to fit buttons
            }
          }}
        >
          <Button 
            component={RouterLink} 
            to="/"
            sx={{ 
              color: '#1a365d', 
              fontWeight: 600,
              px: 1.5,  // Reduce padding
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Home
          </Button>
          
          {isAuthenticated && (
            <Button 
              component={RouterLink} 
              to="/dashboard"
              sx={{ 
                color: '#1a365d', 
                fontWeight: 600,
                px: 1.5,  // Reduce padding
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Dashboard
            </Button>
          )}
          
          <Button 
            component={RouterLink} 
            to="/features"
            sx={{ 
              color: '#1a365d', 
              fontWeight: 600,
              px: 1.5,  // Reduce padding
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Features
          </Button>
          
          <Button 
            component={RouterLink} 
            to="/how-it-works"
            sx={{ 
              color: '#1a365d', 
              fontWeight: 600,
              px: 1.5,  // Reduce padding
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            How It Works
          </Button>
          
          <Button 
            component={RouterLink} 
            to="/pricing"
            sx={{ 
              color: '#1a365d', 
              fontWeight: 600,
              px: 1.5,  // Reduce padding
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Pricing
          </Button>
        </Box>
        
        {/* Action Buttons - Desktop */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center',
            flexShrink: 0,
            ml: 'auto'
          }}
        >
          {isAuthenticated ? (
            <>
              {/* Primary Action Buttons */}
              <Button 
                variant="contained" 
                startIcon={<CloudUploadIcon />}
                component={RouterLink} 
                to="/documents/upload?tab=upload"
                sx={{ 
                  mr: 1,
                  bgcolor: '#1a365d',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: '#0f2a4a'
                  }
                }}
              >
                Upload
              </Button>
              
              <Button 
                variant="contained" 
                startIcon={<AutoAwesomeIcon />}
                component={RouterLink} 
                to="/documents/upload?tab=ai"
                sx={{ 
                  mr: 1,
                  bgcolor: '#2d8cff',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: '#1a7aee'
                  }
                }}
              >
                Generate with AI
              </Button>
              
              <Button 
                variant="outlined"
                onClick={handleMenuClick}
                endIcon={<ArrowDownIcon />}
                sx={{ 
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: '#1a365d',
                  borderRadius: '8px',
                  fontWeight: 600,
                  mr: 1,
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: '#1a365d',
                    bgcolor: 'rgba(26,54,93,0.04)'
                  }
                }}
              >
                More
              </Button>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1.5,
                    borderRadius: '10px',
                    minWidth: '200px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <MenuItem 
                  onClick={handleMenuClose}
                  component={RouterLink}
                  to="/documents"
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(45,140,255,0.08)'
                    }
                  }}
                >
                  My Documents
                </MenuItem>
                <MenuItem 
                  onClick={handleMenuClose}
                  component={RouterLink}
                  to="/templates"
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(45,140,255,0.08)'
                    }
                  }}
                >
                  Templates
                </MenuItem>
              </Menu>
              
              <Button 
                color="inherit" 
                onClick={onLogout}
                sx={{ 
                  color: '#1a365d',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button 
                component={RouterLink} 
                to="/login"
                sx={{ 
                  color: '#1a365d',
                  fontWeight: 600,
                  mr: 1,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Login
              </Button>
              <Button 
                variant="contained" 
                component={RouterLink} 
                to="/register"
                sx={{ 
                  bgcolor: '#2d8cff',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 2,
                  py: 0.75,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: '#1a7aee'
                  }
                }}
              >
                Sign Up
              </Button>
            </>
          )}
        </Box>
        
        {/* Mobile Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              width: '100%',
              maxWidth: '320px'
            }
          }}
        >
          <MenuItem component={RouterLink} to="/" onClick={handleMobileMenuClose}>
            Home
          </MenuItem>
          {isAuthenticated && (
            <MenuItem component={RouterLink} to="/dashboard" onClick={handleMobileMenuClose}>
              Dashboard
            </MenuItem>
          )}
          <MenuItem component={RouterLink} to="/features" onClick={handleMobileMenuClose}>
            Features
          </MenuItem>
          <MenuItem component={RouterLink} to="/how-it-works" onClick={handleMobileMenuClose}>
            How It Works
          </MenuItem>
          <MenuItem component={RouterLink} to="/pricing" onClick={handleMobileMenuClose}>
            Pricing
          </MenuItem>
          
          {isAuthenticated ? (
            <>
              <MenuItem component={RouterLink} to="/documents/upload?tab=upload" onClick={handleMobileMenuClose}>
                Upload Document
              </MenuItem>
              <MenuItem component={RouterLink} to="/documents/upload?tab=ai" onClick={handleMobileMenuClose}>
                Generate with AI
              </MenuItem>
              <MenuItem onClick={onLogout}>
                Logout
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem component={RouterLink} to="/login" onClick={handleMobileMenuClose}>
                Login
              </MenuItem>
              <MenuItem component={RouterLink} to="/register" onClick={handleMobileMenuClose}>
                Sign Up
              </MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;